import React from 'react';

import {
  Button,
  Divider,
  Text,
  TextButton,
  TextField,
  TextTypography,
} from '@wix/wix-ui-tpa/cssVars';
import { Tag as TagIcon } from '@wix/wix-ui-icons-common/on-stage';
import {
  applyCouponToOrder,
  removeCouponFromOrder,
} from '@wix/ambassador-challenges-v1-participant/http';
import {
  ApplyCouponToOrderResponse,
  Participant,
} from '@wix/ambassador-challenges-v1-participant/types';
import { FCWithChildren, formatCurrency } from '@wix/challenges-web-library';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { memberWebAppButtonClick as memberWebAppButtonClickV2 } from '@wix/bi-logger-challenges-member-web/v2';
import { usePromisifyAmbassadorRequest } from '../../../../services/asyncController/usePromisifyAmbassadorRequest';
import { useUser } from '../../../../contexts/User/UserContext';
import { classes, st } from './PaymentCheckout.st.css';
import { ButtonNames } from '../../../../contexts/main/biInterfaces';
import { v4 as uuid } from 'uuid';
import { useChallengeData } from '../../../../contexts/storage-contexts/Challenge';
import { getParticipantOrderId } from '../../../../selectors/participants';
import { useLocation } from '../../../../contexts/Location/LocationContext';
import { ChallengeServerlessData } from '../../../../types/challenge-serverless';
import { useGetResolvedError } from '../../../../services/asyncController/useGetResolvedError';

export interface PaymentCheckoutInfoProps {
  title: string;
  hasCouponsForPrograms: boolean;
  price: string;
  duration: string;
  showOneAppInfo(): void;
  onCouponApplied(coupon: ApplyCouponToOrderResponse): void;
  isPaymentStarted?: boolean;
  initialAppliedCoupon?: ApplyCouponToOrderResponse | null;
}

export const PaymentCheckout: FCWithChildren<PaymentCheckoutInfoProps> = (
  props,
) => {
  const { t } = useTranslation();
  const { challengeData } = useChallengeData();
  const { participant } = useUser();
  const { query } = useLocation();
  const orderId = getParticipantOrderId(participant) || query.orderId;
  const [appliedCoupon, setAppliedCoupon] = React.useState<
    ApplyCouponToOrderResponse & { couponCode: string }
  >(null);
  const [manualCouponAction, setManualCouponAction] =
    React.useState<boolean>(false);
  const { isMobile } = useEnvironment();
  const currency =
    challengeData?.challenge?.settings?.pricing?.singlePayment?.price
      ?.currency || 'USD';
  const bi = useBi();

  // Sync appliedCoupon state with initialAppliedCoupon prop
  // Only sync if user hasn't manually applied/removed a coupon
  React.useEffect(() => {
    if (!manualCouponAction && props.initialAppliedCoupon) {
      setAppliedCoupon({
        ...props.initialAppliedCoupon,
        couponCode: 'Coupon',
      });
    }
  }, [props.initialAppliedCoupon, manualCouponAction]);

  return (
    <section
      className={st(classes.root, {
        mobile: isMobile,
      })}
    >
      <Text
        className={classes.title}
        tagName={isMobile ? 'h1' : 'h2'}
        typography={TextTypography.smallTitle}
      >
        {props.title}
      </Text>
      <time className={classes.duration}>{props.duration}</time>

      {props.hasCouponsForPrograms && !appliedCoupon && (
        <Coupons
          orderId={orderId}
          participant={participant}
          challengeData={challengeData}
          onCouponApplied={(coupon, couponCode) => {
            bi.report(
              memberWebAppButtonClickV2({
                buttonName: ButtonNames.ApplyCoupon,
              }),
            );
            setManualCouponAction(true);
            setAppliedCoupon({ ...coupon, couponCode });
            props.onCouponApplied(coupon);
          }}
          isPaymentStarted={props.isPaymentStarted}
        />
      )}
      {appliedCoupon && (
        <AppliedCoupon
          orderId={orderId}
          coupon={appliedCoupon}
          participant={participant}
          challengeData={challengeData}
          onRemoveCoupon={() => {
            bi.report(
              memberWebAppButtonClickV2({
                buttonName: ButtonNames.RemoveCoupon,
              }),
            );
            setManualCouponAction(true);
            setAppliedCoupon(null);
            props.onCouponApplied(null);
          }}
        />
      )}
      <div aria-hidden="true">
        <Divider className={classes.separator} />
      </div>
      <div className={classes.priceContainer}>
        <span className={classes.priceTitle}>
          {t('payment.page.total-price')}
        </span>
        <span className={classes.priceValue}>
          {appliedCoupon?.total
            ? formatCurrency({ value: appliedCoupon?.total, currency })
            : props.price}
        </span>
      </div>
    </section>
  );
};

interface ICouponsProps {
  orderId: string;
  participant: Participant;
  challengeData: ChallengeServerlessData;
  onCouponApplied(coupon: ApplyCouponToOrderResponse, couponCode: string): void;
  isPaymentStarted?: boolean;
}

const Coupons: FCWithChildren<ICouponsProps> = ({
  orderId,
  onCouponApplied,
  participant,
  challengeData,
  isPaymentStarted,
}) => {
  const { t } = useTranslation();
  const applyCoupon = usePromisifyAmbassadorRequest(
    applyCouponToOrder,
    'applyCouponToOrder',
  );
  const getResolvedError = useGetResolvedError();
  const [couponCode, setCouponCode] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const { isMobile } = useEnvironment();
  const onFormSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const resp = await applyCoupon({
        participantId: participant?.id,
        orderId,
        challengeId: challengeData.challenge.id,
        couponCode,
        actionId: uuid(),
      });
      onCouponApplied(resp, couponCode);
    } catch (err) {
      const resolvedError = await getResolvedError(err);
      setError(resolvedError?.message || 'coupons.form.button.error-generic');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div aria-hidden="true">
        <Divider className={classes.separator} />
      </div>
      <form
        className={classes.coupons}
        onSubmit={(e) => {
          e.preventDefault();
          onFormSubmit();
        }}
      >
        <TextField
          className={classes.couponsInput}
          label={t('coupons.form.input.label')}
          placeholder={t('coupons.form.input.placeholder')}
          value={couponCode}
          withClearButton={true}
          disabled={loading || isPaymentStarted}
          error={!!error}
          newErrorMessage={true}
          errorMessage={t(error)}
          onClear={() => {
            setCouponCode('');
            setError(null);
          }}
          onChange={(e) => {
            setCouponCode(e.target.value);
            setError(null);
          }}
        />
        <Button
          disabled={!couponCode || loading || isPaymentStarted}
          className={classes.couponsButton}
          fullWidth={isMobile}
          type="submit"
        >
          {t('coupons.form.button.apply')}
        </Button>
      </form>
    </>
  );
};

interface IAppliedCouponProps {
  coupon: ApplyCouponToOrderResponse & { couponCode: string };
  orderId: string;
  participant: Participant;
  challengeData: ChallengeServerlessData;
  onRemoveCoupon(): void;
}

const AppliedCoupon: FCWithChildren<IAppliedCouponProps> = ({
  orderId,
  coupon,
  onRemoveCoupon,
  participant,
  challengeData,
}) => {
  const { t } = useTranslation();
  const removeCoupon = usePromisifyAmbassadorRequest(
    removeCouponFromOrder,
    'removeCouponFromOrder',
  );
  const [loading, setLoading] = React.useState(false);
  const currency =
    challengeData?.challenge?.settings?.pricing?.singlePayment?.price
      ?.currency || 'USD';

  const onRemoveClick = async () => {
    setLoading(true);
    try {
      await removeCoupon({
        orderId,
        challengeId: challengeData.challenge.id,
        couponId: coupon.couponId,
        participantId: participant?.id,
        actionId: uuid(),
      });
      onRemoveCoupon();
    } catch (err) {
      console.log('error on remove coupon: ', err?.response);
    }

    setLoading(false);
  };

  return (
    <div>
      <Divider className={classes.separator} />
      <div className={classes.couponAppliedContainer}>
        <span className={classes.couponCode}>
          <TagIcon />
          <span>{coupon.couponCode}</span>
        </span>
        <TextButton disabled={loading} onClick={onRemoveClick}>
          {t('coupons.form.button.remove')}
        </TextButton>
      </div>
      <Divider className={classes.separator} />
      <dl className={classes.couponsDiscountContainer}>
        <dt className={classes.couponsTitle}>
          {t('coupons.applied.subtotal')}
        </dt>
        <dd className={classes.couponsValue}>
          {formatCurrency({ value: coupon.subTotal, currency })}
        </dd>
      </dl>
      <dl className={classes.couponsDiscountContainer}>
        <dt className={classes.couponsTitle}>
          {t('coupons.applied.discount', { couponName: coupon.couponCode })}
        </dt>
        <dd className={classes.couponsValue}>
          {' '}
          -{formatCurrency({ value: coupon.discount, currency })}
        </dd>
      </dl>
    </div>
  );
};
